import { ref } from 'vue'
import { useNavigationPermissions } from '@/composables/permissions/useNavigationPermissions'
import type { NavigationPermissions } from '@/composables/permissions/useNavigationPermissions'
import { captureSentryException } from '@/utils/sentry'
import { isNotBlank } from '@/utils/String'
import type { Organization } from 'shared/src'

interface AuthResponse {
  authenticated: boolean
  first_name?: string
  last_name?: string
  primary_language?: string
  organization?: Organization
  user_id?: string
}

interface PermissionsResponse {
  permissions?: NavigationPermissions
}

interface LoginResponse {
  permissions?: NavigationPermissions
  primary_language?: string
  message?: string
}

const firstName = ref('')
const lastName = ref('')
const primaryLanguage = ref<string | undefined>()
const organization = ref<Organization | undefined>()
const userId = ref('')

export const useAuthBff = () => {
  const isAuthenticated = ref<boolean>(false)
  const loading = ref(false)
  const error = ref<string>('')
  const { setPermissions, clearPermissions } = useNavigationPermissions()

  const checkAuth = async (): Promise<boolean> => {
    loading.value = true
    error.value = ''

    try {
      const response = await fetch(`/auth/authenticated`, {
        method: 'GET',
        credentials: 'include',
      })

      if (response.status === 401) {
        isAuthenticated.value = false
        error.value = 'No active session'
        clearPermissions()
        return false
      }

      if (!response.ok) {
        throw new Error(`HTTP ${String(response.status)}`)
      }

      const data = (await response.json()) as AuthResponse
      isAuthenticated.value = data.authenticated
      firstName.value = data.first_name ?? ''
      lastName.value = data.last_name ?? ''
      primaryLanguage.value = isNotBlank(data.primary_language)
        ? data.primary_language
        : primaryLanguage.value
      organization.value = data.organization
      userId.value = data.user_id ?? ''

      // Fetch permissions if authenticated
      if (isAuthenticated.value) {
        const permResponse = await fetch(`/auth/permissions`, {
          method: 'GET',
          credentials: 'include',
        })

        if (permResponse.ok) {
          const permData = (await permResponse.json()) as PermissionsResponse
          if (permData.permissions) {
            setPermissions(permData.permissions)
          }
        }
      }

      return isAuthenticated.value
    } catch (err: unknown) {
      console.error(err)
      void captureSentryException(err, {
        tags: { component: 'auth' },
        extra: { context: 'checkAuth' },
      })
      error.value = err instanceof Error ? err.message : 'Unexpected error'
      isAuthenticated.value = false
      clearPermissions()
      return false
    } finally {
      loading.value = false
    }
  }

  const login = async (identityToken: string): Promise<boolean> => {
    loading.value = true
    error.value = ''

    try {
      const response = await fetch(`/auth/login-with-identity-token`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          identity_token: identityToken,
        }),
      })

      if (!response.ok) {
        const errData = (await response.json().catch(() => null)) as LoginResponse | null
        throw new Error(
          isNotBlank(errData?.message) ? errData.message : `HTTP ${String(response.status)}`,
        )
      }

      // Get permissions and language from login response
      const data = (await response.json()) as LoginResponse
      if (data.permissions) {
        setPermissions(data.permissions)
      }
      if (data.primary_language) {
        primaryLanguage.value = data.primary_language
      }

      const authOk = await checkAuth()
      if (!authOk) {
        isAuthenticated.value = false
        error.value = 'Invalide session after login'
        clearPermissions()
        return false
      }

      return true
    } catch (err: unknown) {
      console.error(err)
      void captureSentryException(err, {
        tags: { component: 'auth', operation: 'login' },
      })
      error.value = err instanceof Error ? err.message : 'Unexpected error'
      isAuthenticated.value = false
      clearPermissions()
      return false
    } finally {
      loading.value = false
    }
  }

  return {
    isAuthenticated,
    loading,
    error,
    primaryLanguage,
    firstName,
    lastName,
    userId,
    organization,
    checkAuth,
    login,
  }
}
